import { useCallback, useMemo, useRef } from "react";
import { useQuery } from "@tanstack/react-query";
import { fetchChecklistDetail, fetchChecklistStatusMatrix } from "@/react/components/entityViews/checklist/api/checklist.service";
import { checklistKeys } from "@/react/components/entityViews/checklist/api/checklist.query-keys";
import { useBoardUsers } from "@/react/hooks/useBoardUsers";
import type { CheckGridDefinition, CheckGridMatrix, CheckGridOverviewItem } from "../types";
import { getMatrixQueryParams } from "../utils/dayWindow";
import {
  definitionFromMatrixAndOverview,
  enrichMatrixFromRetrieve,
  matrixFromStatusMatrix,
  retrieveForMatrixFromDetail,
  type StatusMatrixPayload
} from "../utils/apiAdapters";

function readNumericBoardId(boardId: number | string | null): number | null {
  if (boardId == null || boardId === "") return null;
  const n = typeof boardId === "number" ? boardId : Number(boardId);
  return Number.isFinite(n) ? n : null;
}

export function useCheckgridDetail(
  boardId: number | string | null,
  gridId: number | null,
  day: Date,
  timeZone?: string | null,
  overview?: CheckGridOverviewItem | null
) {
  const boardUsers = useBoardUsers();
  const overviewRef = useRef(overview);
  overviewRef.current = overview;
  const boardUsersRef = useRef(boardUsers);
  boardUsersRef.current = boardUsers;

  const numericBoardId = readNumericBoardId(boardId);
  const dayKey = `${day.getFullYear()}-${day.getMonth()}-${day.getDate()}`;
  // dayKey (not `day`) — calendar identity for Today vs history `to`.
  const matrixParams = useMemo(() => getMatrixQueryParams(day, timeZone), [day, dayKey, timeZone]);
  const matrixTo = matrixParams?.to ?? null;
  const canQuery = numericBoardId != null && gridId != null;

  const matrixQuery = useQuery({
    queryKey: canQuery
      ? checklistKeys.statusMatrix(numericBoardId, gridId, matrixTo)
      : [...checklistKeys.all, "status-matrix", "idle"],
    queryFn: () =>
      fetchChecklistStatusMatrix({
        boardId: numericBoardId!,
        checklistId: gridId!,
        to: matrixParams?.to
      }),
    enabled: canQuery,
    staleTime: 30_000,
    retry: false
  });

  // Same key as EM tile/editor — share retrieve cache; tolerate failure (matrix-only paint).
  const detailQuery = useQuery({
    queryKey: canQuery ? checklistKeys.detail(numericBoardId, gridId) : [...checklistKeys.all, "detail", "idle"],
    queryFn: () => fetchChecklistDetail({ boardId: numericBoardId!, checklistId: gridId! }),
    enabled: canQuery,
    staleTime: 30_000,
    retry: false
  });

  // Current-period cells get checked state from retrieve. Painting matrix-only first
  // then enriching after retrieve lands flashes empty → filled (skeleton → glitch → data).
  const waitingForRetrieveFill =
    canQuery && matrixParams == null && detailQuery.isPending && !detailQuery.isError;

  const { definition, matrix } = useMemo((): {
    definition: CheckGridDefinition | null;
    matrix: CheckGridMatrix | null;
  } => {
    if (!canQuery || matrixQuery.data == null || waitingForRetrieveFill) {
      return { definition: null, matrix: null };
    }

    const payload = matrixQuery.data as StatusMatrixPayload;
    const overviewSnap = overviewRef.current;
    const checklist = retrieveForMatrixFromDetail(detailQuery.data);
    const detailChecklist = detailQuery.data?.checklist;

    const { nameOf: boardUserName } = boardUsersRef.current;
    const nameOf = (id?: number | null) => boardUserName(id) ?? null;

    // Retrieve checked state is current-period only — never paint it onto a history `to` window.
    const adapted = enrichMatrixFromRetrieve(
      matrixFromStatusMatrix(payload, gridId, overviewSnap ?? null),
      checklist,
      boardUserName,
      { fillCheckedFromRetrieve: matrixParams == null }
    );

    const recurrence =
      (checklist?.recurrence as CheckGridOverviewItem["recurrence"] | undefined) ||
      (payload.recurrence as CheckGridOverviewItem["recurrence"] | undefined) ||
      overviewSnap?.recurrence ||
      "none";

    const title = checklist?.checklist_title ?? checklist?.title;
    const createdAt =
      detailChecklist?.createdAt != null
        ? String(detailChecklist.createdAt)
        : overviewSnap?.created_at ?? null;
    const updatedAt = detailChecklist?.updatedAt != null ? String(detailChecklist.updatedAt) : null;

    const baseOverview: CheckGridOverviewItem =
      overviewSnap ??
      ({
        id: gridId,
        name: title ?? `Checklist ${gridId}`,
        subject_type: checklist?.task ? "task" : checklist?.client ? "client" : "resource",
        recurrence,
        recurrence_weekdays: checklist?.recurrence_weekdays ?? payload.recurrence_weekdays ?? undefined,
        archived: Boolean(checklist?.archived),
        can_view_grant: checklist?.can_view_grant,
        can_edit_grant: checklist?.can_edit_grant,
        created_at: createdAt ?? undefined,
        resource_id: checklist?.resource ?? null,
        task_id: checklist?.task ?? null,
        client_id: checklist?.client ?? null,
        summary: adapted.summary
      } satisfies CheckGridOverviewItem);

    const withMeta: CheckGridOverviewItem = {
      ...baseOverview,
      name: title ?? baseOverview.name,
      recurrence,
      recurrence_weekdays: checklist?.recurrence_weekdays ?? payload.recurrence_weekdays ?? baseOverview.recurrence_weekdays,
      summary: adapted.summary
    };

    return {
      matrix: adapted,
      definition: {
        ...definitionFromMatrixAndOverview(withMeta, adapted, {
          archived: checklist?.archived,
          can_view_grant: checklist?.can_view_grant,
          can_edit_grant: checklist?.can_edit_grant
        }),
        created_at: createdAt ?? baseOverview.created_at ?? null,
        updated_at: updatedAt,
        creator_name: nameOf(detailChecklist?.creator),
        updater_name: nameOf(detailChecklist?.updater)
      }
    };
  }, [canQuery, detailQuery.data, gridId, matrixParams, matrixQuery.data, waitingForRetrieveFill]);

  const isLoading = Boolean(canQuery && (matrixQuery.isPending || waitingForRetrieveFill));
  const error =
    matrixQuery.error != null
      ? (matrixQuery.error as { response?: { data?: { detail?: string } }; message?: string }).response?.data
          ?.detail ||
        matrixQuery.error.message ||
        "Unable to load this checklist."
      : null;

  // refetch() ignores `enabled`; without a selected checklist it would request /checklists/null/.
  const refresh = useCallback(async () => {
    if (!canQuery) return;
    await Promise.all([matrixQuery.refetch(), detailQuery.refetch()]);
  }, [canQuery, detailQuery, matrixQuery]);

  return { definition, matrix, isLoading, error, refresh };
}
